import React, { useMemo } from 'react';

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_WIDTH = 28; // px per day — enough room for a tooltip hover target without the chart getting absurdly wide

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysBetween(a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / DAY_MS);
}

function formatTick(date) {
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

/**
 * Generic day-scale horizontal Gantt. No date-picking/drag — view-only bars.
 * rows: [{ id, label, segments: [{ start: Date, end: Date, color, tooltip, dashed? }] }]
 */
export default function SimpleGanttChart({ rows, emptyMessage = 'Nothing to show yet.' }) {
  const { rangeStart, totalDays, weekTicks } = useMemo(() => {
    const allDates = rows.flatMap(r => r.segments.flatMap(s => [s.start, s.end]));
    if (allDates.length === 0) return { rangeStart: new Date(), totalDays: 0, weekTicks: [] };

    const minDate = startOfDay(new Date(Math.min(...allDates.map(d => new Date(d).getTime()))));
    const maxDate = startOfDay(new Date(Math.max(...allDates.map(d => new Date(d).getTime()))));
    // Pad a few days on each side so bars don't render flush against the edges.
    const start = new Date(minDate.getTime() - 2 * DAY_MS);
    const end = new Date(maxDate.getTime() + 2 * DAY_MS);
    const total = Math.max(daysBetween(start, end), 1);

    const ticks = [];
    for (let d = 0; d <= total; d += 7) {
      ticks.push({ offset: d, date: new Date(start.getTime() + d * DAY_MS) });
    }
    return { rangeStart: start, totalDays: total, weekTicks: ticks };
  }, [rows]);

  if (rows.length === 0) {
    return <p className="text-sm text-[#706E6B] py-8 text-center">{emptyMessage}</p>;
  }

  const trackWidth = totalDays * DAY_WIDTH;

  return (
    <div className="border border-[#E5E5E5] rounded-lg overflow-hidden bg-white">
      <div className="overflow-x-auto">
        <div style={{ minWidth: trackWidth + 180 }}>
          {/* Week-tick header */}
          <div className="flex border-b border-[#F3F2F2] bg-[#F7F9FB] sticky top-0">
            <div className="w-[180px] flex-shrink-0 px-3 py-2 text-xs font-semibold text-[#706E6B]">Item</div>
            <div className="relative flex-1" style={{ height: 28, width: trackWidth }}>
              {weekTicks.map((t, i) => (
                <div key={i} className="absolute top-0 h-full border-l border-[#E5E5E5] text-[10px] text-[#706E6B] pl-1 pt-1.5"
                  style={{ left: t.offset * DAY_WIDTH }}>
                  {formatTick(t.date)}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {rows.map(row => (
            <div key={row.id} className="flex border-b border-[#F3F2F2] hover:bg-[#F7F9FB]/50">
              <div className="w-[180px] flex-shrink-0 px-3 py-2.5 text-xs font-medium text-[#444444] truncate" title={row.label}>
                {row.label}
              </div>
              <div className="relative flex-1" style={{ height: 40, width: trackWidth }}>
                {row.segments.map((seg, i) => {
                  const left = daysBetween(rangeStart, seg.start) * DAY_WIDTH;
                  const width = Math.max(daysBetween(seg.start, seg.end) * DAY_WIDTH, 6);
                  return (
                    <div
                      key={i}
                      title={seg.tooltip}
                      className={`absolute top-2 h-4 rounded-md ${seg.dashed ? 'border-2 border-dashed bg-transparent' : ''}`}
                      style={{
                        left, width,
                        backgroundColor: seg.dashed ? 'transparent' : seg.color,
                        borderColor: seg.dashed ? seg.color : undefined,
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
